import { debounce } from '../utils';

export default (livewireComponent = null, options = {}) => ({
  selectedOptions: livewireComponent ?? [],
  isMenuOpen: false,
  bgColor: 'bg-' + (options.highlightColor || 'brand-tertiary-300'),
  highlightColor: options.highlightColor || 'brand-tertiary-300',
  disabled: options.disabled || false,
  name: options.name || '',
  selectLabel: options.label || '',
  defaultPlaceholder: options.defaultPlaceholder || 'Select',
  // Translated suffix for the multi-selection display label ("3 products selected").
  // Threaded through the options bag from the blade — defaults to English only if a
  // caller bypasses the blade component (no untranslated copy reaches the UI in practice).
  selectedSuffix: options.selectedSuffix || 'selected',
  searchable: options.searchable !== false,
  searchInput: '',
  noResults: false,
  loading: false,
  fetchError: false,
  displayLabel: '',

  wireMethod: options.wireMethod || '',
  extraArgs: Array.isArray(options.extraArgs) ? options.extraArgs : [],
  pageSize: options.pageSize || 50,
  currentPage: 1,
  serverOptions: [],
  hasMore: false,
  totalCount: 0,
  /**
   * Index of the option under roving keyboard focus — the listbox
   * aria-activedescendant target. DOM focus stays on the search input (combobox),
   * so arrow keys move this index rather than moving focus. Reset to 0 on each fresh
   * result set; preserved across "load more" so the user's place isn't lost.
   */
  focusedIndex: 0,
  /**
   * Monotonic token to discard stale fetch results. When the user types fast
   * enough that a newer query is dispatched before the older one resolves, the
   * older response would otherwise overwrite the newer list (and the loading
   * spinner would clear early). Each fetchOptions() bumps the token; on resolve
   * we drop the response unless the token still matches.
   */
  _currentRequestToken: 0,

  /**
   * Map of `{ value: { label, children? } }` for resolving chip labels of values
   * that aren't on the current server page (edit-mode hydration).
   */
  selectedMeta: options.initialSelectedMeta && typeof options.initialSelectedMeta === 'object'
    ? Object.assign({}, options.initialSelectedMeta)
    : {},

  isSelected(value) {
    if (!Array.isArray(this.selectedOptions)) return false;
    const v = String(value);
    return this.selectedOptions.some(s => String(s) === v);
  },

  labelFor(value) {
    const v = String(value);
    if (this.selectedMeta[v] && this.selectedMeta[v].label) {
      return this.selectedMeta[v].label;
    }
    const found = this.serverOptions.find(o => String(o.value) === v);
    return found ? found.label : v;
  },

  refreshDisplayLabel() {
    const selected = Array.isArray(this.selectedOptions) ? this.selectedOptions : [];
    if (selected.length === 0) {
      this.displayLabel = this.defaultPlaceholder;
      return;
    }
    if (selected.length === 1) {
      this.displayLabel = this.labelFor(selected[0]);
      return;
    }
    this.displayLabel = `${selected.length} ${this.selectLabel || this.name} ${this.selectedSuffix}`;
  },

  async fetchOptions(search = '', page = 1, append = false) {
    if (!this.wireMethod) return;
    const token = ++this._currentRequestToken;
    this.fetchError = false;
    this.loading = true;
    try {
      const result = await this.$wire[this.wireMethod](search, page, ...this.extraArgs);
      // Drop if a newer fetch has been dispatched in the meantime.
      if (token !== this._currentRequestToken) return;
      if (!result || !Array.isArray(result.options)) {
        this.fetchError = true;
        if (!append) this.serverOptions = [];
        return;
      }

      if (append) {
        this.serverOptions = [...this.serverOptions, ...result.options];
      } else {
        this.serverOptions = result.options;
        this.focusedIndex = 0;
        if (this.$refs.menu) this.$refs.menu.scrollTop = 0;
      }

      // Cache labels for every server-returned option so chips always resolve
      this.serverOptions.forEach(o => {
        const key = String(o.value);
        if (!this.selectedMeta[key]) {
          this.selectedMeta[key] = { label: o.label };
        }
      });

      this.hasMore = !!result.hasMore;
      // null = source couldn't determine the full count (treat as unknown, not 0)
      this.totalCount = result.total ?? null;
      this.currentPage = page;
      this.noResults = this.serverOptions.length === 0;

      this.refreshDisplayLabel();
    } catch (e) {
      if (token !== this._currentRequestToken) return;
      this.fetchError = true;
      if (!append) this.serverOptions = [];
    } finally {
      // Only clear the spinner if this is still the latest request — otherwise
      // we'd hide loading while the newer request is still in flight.
      if (token === this._currentRequestToken) this.loading = false;
    }
  },

  async loadMore() {
    if (!this.hasMore || this.loading) return;
    await this.fetchOptions(this.searchInput, this.currentPage + 1, true);
  },

  toggleOption(value) {
    const v = String(value);
    const current = Array.isArray(this.selectedOptions) ? this.selectedOptions.slice() : [];
    const idx = current.findIndex(s => String(s) === v);
    if (idx === -1) {
      current.push(v);
      // Sync the label to Livewire so save time doesn't need to re-fetch it.
      // The label is already in memory — either from the just-rendered server page or
      // from the hydrated selected-meta if this value was pre-selected.
      const opt = this.serverOptions.find(o => String(o.value) === v);
      const label = opt && opt.label
        ? opt.label
        : (this.selectedMeta[v] && this.selectedMeta[v].label ? this.selectedMeta[v].label : v);
      this.selectedMeta[v] = { label };
      if (this.$wire && typeof this.$wire.recordSourceOptionLabel === 'function') {
        this.$wire.recordSourceOptionLabel(this.name, v, label);
      }
    } else {
      current.splice(idx, 1);
    }
    this.selectedOptions = current;
  },

  clearSelection() {
    this.selectedOptions = [];
  },

  // --- Listbox roving keyboard navigation (arrow keys fire on the combobox input) ---
  moveFocus(delta) {
    if (!this.serverOptions.length) return;
    const last = this.serverOptions.length - 1;
    this.focusedIndex = Math.max(0, Math.min(last, this.focusedIndex + delta));
    this.scrollFocusedIntoView();
  },

  focusEdge(index) {
    if (!this.serverOptions.length) return;
    const last = this.serverOptions.length - 1;
    this.focusedIndex = Math.max(0, Math.min(last, index));
    this.scrollFocusedIntoView();
  },

  selectFocused() {
    const opt = this.serverOptions[this.focusedIndex];
    if (opt) this.toggleOption(opt.value);
  },

  scrollFocusedIntoView() {
    this.$nextTick(() => {
      const opts = this.$refs.menu?.querySelectorAll('[role="option"]');
      const el = opts && opts[this.focusedIndex];
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ block: 'nearest' });
      }
    });
  },

  focusInput() {
    const input = this.$refs.input;
    if (input) input.focus();
  },

  handleBtnClick() {
    if (this.disabled) return;
    if (this.isMenuOpen) {
      this.closeMenu();
    } else {
      this.isMenuOpen = true;
      this.$nextTick(() => this.onMenuOpen());
    }
  },

  onMenuOpen() {
    this.searchInput = '';
    this.fetchOptions('', 1, false);
    this.focusInput();
  },

  closeMenu() {
    this.isMenuOpen = false;
    this.searchInput = '';
  },

  handleFocusOut(event) {
    if (!this.isMenuOpen) return;
    const focusWithin = this.$el.contains(event.relatedTarget);
    if (!focusWithin) {
      this.closeMenu();
    }
  },

  handleEscape() {
    if (this.isMenuOpen) this.closeMenu();
  },

  init() {
    // Listen for external invalidation (e.g. parent param changed → dependents stale).
    // Livewire.on returns an unsubscribe fn — capture it and call it on teardown so a
    // destroyed+recreated Alpine root doesn't leave stale closures firing on detached state.
    let unsubscribe = null;
    if (window.Livewire) {
      unsubscribe = window.Livewire.on('source-options-invalidated', (payload) => {
        const data = Array.isArray(payload) ? payload[0] : payload;
        const targetParam = data && (data.param ?? data[0]);
        if (targetParam && targetParam !== this.name) return;
        this.serverOptions = [];
        this.currentPage = 1;
        if (this.isMenuOpen) this.fetchOptions(this.searchInput, 1, false);
      });
    }

    this.$watch('selectedOptions', () => this.refreshDisplayLabel());
    this.$watch('searchInput', debounce(value => {
      if (!this.isMenuOpen) return;
      this.fetchOptions(value, 1, false);
    }, 300));

    this.refreshDisplayLabel();

    this.$root?.addEventListener?.('alpine:destroy', () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    });
  },
});
